/**
 * The parameter FAMILIES a page or a rail entry opens the register on — `docs/adr/9064` (bata-reqs
 * issue 38 R1, owner rulings 2026-10-17).
 *
 * A family is a key prefix inside one app. The register (`/foundations/parameters`) opened with
 * `?app=<app>&keys=<prefix>` shows that app's keys under the prefix, and nothing else; editing stays
 * the register's own (a proposal, then an approval, ADR 9026).
 *
 * Two kinds:
 *
 * - **an entry's own** (`ownEntry: true`): «Transizione a fine vita» and «Uscita verso outlet» each
 *   have a «Parametri» sub-entry in Pricing, and their keys are shown THERE and not in Pricing's
 *   general «Parametri» — so no key is shown in two places (ruling R1.3);
 *   «Promozioni e campagne» has one too (owner rulings 2026-10-17, second set, R1; `docs/adr/9055`
 *   amended): the campaign keys and the promotion-mechanic vocabulary its two screens read, which
 *   leave Management's «Parametri generali»;
 * - **a link only**: the markdown keys (`markdown.*`) stay in Pricing's «Parametri» («Prezzo e
 *   ribasso»), and the phase register's one key stays there too; the end-of-life pages link to
 *   them filtered («Parametri del ribasso», «Parametri del registro»).
 *
 * Here, beside `rail-apps.ts`, because the rail (`src/app/chrome/`), the register (ch00) and the
 * end-of-life pages (ch05) all read it, and no feature may import another (G11). No runtime
 * imports: the chrome is in every page's first bundle. The register path and the two query keys
 * are spelled here as literals, not as `…_PARAM` constants: an end-of-life page LINKS to the
 * register and reads neither key (the URL-parameter census, ADR 9054). `rail-layout.test.ts` and
 * `parameter-families.test.ts` assert they still equal ch00's constants.
 */

export const PARAMETER_FAMILY_IDS = ['lifecycleTransition', 'outletExit', 'lifecyclePhase', 'markdown', 'campaigns', 'health'] as const;
export type ParameterFamilyId = (typeof PARAMETER_FAMILY_IDS)[number];

export interface ParameterFamily {
  readonly id: ParameterFamilyId;
  /** The key prefix, matched literally, dot included. It is also the family's `?keys=` value. */
  readonly prefix: string;
  /**
   * Exact keys outside the prefix the family also holds — the promotion-mechanic vocabulary, read by
   * the same two screens as the campaign keys but named under `promotion.`. Empty for most families.
   */
  readonly extraKeys: readonly string[];
  /** The register tab the family's keys belong to (`foundations/parameter-apps.ts`, ADR 9025). */
  readonly app: 'pricing' | 'management';
  /** The family's subject, in the reader's language — the register's note and its scope chip. */
  readonly nameKey: string;
  /** A page's link to the family («Parametri della transizione»). */
  readonly linkKey: string;
  /** Whether a rail entry owns the family's «Parametri», so the app's general one leaves it out. */
  readonly ownEntry: boolean;
}

export const PARAMETER_FAMILIES: Readonly<Record<ParameterFamilyId, ParameterFamily>> = {
  lifecycleTransition: {
    id: 'lifecycleTransition', prefix: 'exit.lifecycle_transition.', app: 'pricing',
    nameKey: 'nav.parameterFamily.lifecycleTransition.name',
    linkKey: 'nav.parameterFamily.lifecycleTransition.link',
    // The process calendar's run-ahead of the transition window (bata-reqs issue 38 R2, ADR 9068).
    extraKeys: ['process_calendar.transition.run_ahead_days'], ownEntry: true,
  },
  outletExit: {
    id: 'outletExit', prefix: 'exit.outlet_exit.', app: 'pricing',
    nameKey: 'nav.parameterFamily.outletExit.name',
    linkKey: 'nav.parameterFamily.outletExit.link',
    // The process calendar's run-ahead of the outlet window (ADR 9068).
    extraKeys: ['process_calendar.outlet.run_ahead_days'], ownEntry: true,
  },
  lifecyclePhase: {
    id: 'lifecyclePhase', prefix: 'exit.lifecycle_phase.', app: 'pricing',
    nameKey: 'nav.parameterFamily.lifecyclePhase.name',
    linkKey: 'nav.parameterFamily.lifecyclePhase.link', extraKeys: [], ownEntry: false,
  },
  markdown: {
    id: 'markdown', prefix: 'markdown.', app: 'pricing',
    nameKey: 'nav.parameterFamily.markdown.name',
    linkKey: 'nav.parameterFamily.markdown.link', extraKeys: [], ownEntry: false,
  },
  // «Promozioni e campagne ▸ Parametri» (owner rulings 2026-10-17, second set): the campaign
  // response and simulation keys, and the mechanic vocabulary the promotion calendar and the
  // campaign simulation read. NOT `promotion_minimum_observations` / `promotion_required_windows`:
  // despite the name they govern the forecast model's champion–challenger promotion
  // (bata-platform `postseason.decide_promotion`) and stay in Management's «Qualità della previsione».
  campaigns: {
    id: 'campaigns', prefix: 'campaign.', app: 'management',
    nameKey: 'nav.parameterFamily.campaigns.name',
    linkKey: 'nav.parameterFamily.campaigns.link',
    extraKeys: ['promotion.mechanic_vocabulary'], ownEntry: true,
  },
  // «Salute ▸ Parametri» (bata-reqs ADR 9003 O5, docs/25 §5.6; bata-platform ADR 9054): the management
  // targets that colour the Health reports' figures, and the amber tolerance they share — each per market or
  // per department family of the market (bata-platform ADR 9055) — and Coverage's weeks-of-cover bands
  // (ADR 9053 amended), which are not a target and so sit outside the prefix as an extra key. Management's
  // keys by the register's fallback (Health decides nothing, so it has no tab and no queue of its own —
  // a proposed target goes to Management's queue), shown here and not in «Parametri generali».
  health: {
    id: 'health', prefix: 'health.target.', app: 'management',
    nameKey: 'nav.parameterFamily.health.name',
    linkKey: 'nav.parameterFamily.health.link',
    extraKeys: ['health.coverage.cover_band_weeks'], ownEntry: true,
  },
};

/** Whether `parameterKey` is one of `family`'s keys: under its prefix, or one of its extra keys. Pure. */
export function familyHolds(family: ParameterFamily, parameterKey: string): boolean {
  return parameterKey.startsWith(family.prefix) || family.extraKeys.includes(parameterKey);
}

/** The families a rail entry owns — left out of their app's general «Parametri». */
export const OWN_ENTRY_FAMILIES: readonly ParameterFamily[] = PARAMETER_FAMILY_IDS
  .map((id) => PARAMETER_FAMILIES[id])
  .filter((family) => family.ownEntry);

/** The family a `?keys=` value names, or `null` for none or a prefix no family has. Pure. */
export function familyOfPrefix(prefix: string | null): ParameterFamily | null {
  if (prefix === null) return null;
  return PARAMETER_FAMILY_IDS.map((id) => PARAMETER_FAMILIES[id]).find((family) => family.prefix === prefix) ?? null;
}

/** The family a rail entry owns that a key falls in, or `null`. Pure. */
export function ownEntryFamilyOf(parameterKey: string): ParameterFamily | null {
  return OWN_ENTRY_FAMILIES.find((family) => familyHolds(family, parameterKey)) ?? null;
}

/** The register's path, as ch00 mounts it. */
const REGISTER_PATH = '/foundations/parameters';

/** The link that opens the register on one family — `?app=` and `?keys=`. Pure. */
export function parameterFamilyHref(id: ParameterFamilyId): string {
  const family = PARAMETER_FAMILIES[id];
  return `${REGISTER_PATH}?${new URLSearchParams({ app: family.app, keys: family.prefix }).toString()}`;
}

/**
 * «Scale di sconto» (`docs/adr/9066`): the discount-ladder panel on Pricing's «Parametri», where its
 * section carries this id. «Prezzo e ribasso» links to it (owner rulings 2026-10-17, second set,
 * R3.3) — the ladders are not a key family, so the link names the panel rather than a `?keys=`.
 */
export const PRICE_LADDERS_ANCHOR = 'price-ladders';

/** The link to «Scale di sconto»: Pricing's register, scrolled to the panel. Pure. */
export function priceLaddersHref(): string {
  return `${REGISTER_PATH}?${new URLSearchParams({ app: 'pricing' }).toString()}#${PRICE_LADDERS_ANCHOR}`;
}